/**
 * Fine Appeal — multi-step modal for analysing a fine and drafting an
 * appeal letter.
 *
 * Flow:
 *   1. Upload step — user picks/takes a photo of the fine.
 *   2. Analysing step — image is sent to the server (loading spinner).
 *   3. Result step — extracted fields + appeal-success score + the letter.
 *      User can: copy text, download .txt, or edit the letter inline.
 *
 * Auto-submission to gov.il is out of scope for the MVP; we equip the
 * user with the letter and instructions for where to submit it.
 */

import * as service from './fineAppeal.service.js';
import { escape as esc } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';
import { prepareImage } from '../../shared/imageUtil.js';

const log = createLogger('fine-appeal.view');

const OVERLAY_ID = 'fineAppealOverlay';

/** @type {'upload' | 'analysing' | 'result' | 'error'} */
let step = 'upload';
let currentResult = null;
let currentError = '';

/** Open the modal at the upload step. */
export function open() {
  const overlay = ensureModal();
  document.body.appendChild(overlay);
  overlay.classList.add('open');
  step = 'upload';
  currentResult = null;
  currentError = '';
  render();
}

function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.remove('open');
}

function ensureModal() {
  let ov = document.getElementById(OVERLAY_ID);
  if (ov) return ov;
  ov = document.createElement('div');
  ov.id = OVERLAY_ID;
  ov.className = 'fine-overlay';
  ov.innerHTML = `
    <aside class="fine-modal" role="dialog" aria-modal="true" aria-labelledby="fineTitle">
      <header class="fine-header">
        <div>
          <div class="fine-title" id="fineTitle">🎫 ערעור קנס</div>
          <div class="fine-sub">העלה תמונה — AI יכתוב מכתב ערעור משפטי</div>
        </div>
        <button type="button" class="fine-close" aria-label="סגור">✕</button>
      </header>
      <div class="fine-body" id="fineBody"></div>
    </aside>
  `;
  ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
  ov.querySelector('.fine-close')?.addEventListener('click', close);
  return ov;
}

function render() {
  const body = document.getElementById('fineBody');
  if (!body) return;
  if (step === 'upload') renderUpload(body);
  else if (step === 'analysing') renderAnalysing(body);
  else if (step === 'result') renderResult(body);
  else if (step === 'error') renderError(body);
}

function renderUpload(body) {
  body.innerHTML = `
    <div class="fine-upload">
      <div class="fine-upload-emoji">📸</div>
      <div class="fine-upload-title">צלם או העלה תמונה של הקנס</div>
      <div class="fine-upload-sub">
        זה יכול להיות צילום של הדף המקורי, או צילום מסך מאפליקציית התשלום.
        AI ינתח את הקנס, יזהה אפשרויות לערעור, ויכתוב לך מכתב מוכן להגשה.
      </div>
      <input type="file" id="fineFileInput" accept="image/*" capture="environment" hidden>
      <button type="button" class="fine-upload-btn" id="fineUploadBtn">
        <span class="fine-upload-btn-icon">📸</span>
        <span>בחר תמונה</span>
      </button>
      <div class="fine-tips">
        <div class="fine-tip">💡 וודא שכל הקנס נראה בתמונה</div>
        <div class="fine-tip">💡 צילום ברור = ניתוח מדויק יותר</div>
        <div class="fine-tip">🔒 התמונה לא נשמרת — רק נשלחת לניתוח חד-פעמי</div>
      </div>
    </div>
  `;
  const input = /** @type {HTMLInputElement | null} */ (document.getElementById('fineFileInput'));
  body.querySelector('#fineUploadBtn')?.addEventListener('click', () => input?.click());
  input?.addEventListener('change', onFilePicked);
}

function renderAnalysing(body) {
  body.innerHTML = `
    <div class="fine-analysing">
      <div class="fine-analysing-spinner"></div>
      <div class="fine-analysing-title">AI מנתח את הקנס שלך…</div>
      <div class="fine-analysing-sub">
        זה לוקח בערך 10-20 שניות. בודק את הסעיף החוקי, מחפש פגמים בקנס,
        ומכין מכתב ערעור מנוסח כראוי לפי הרשות המנפיקה.
      </div>
    </div>
  `;
}

function renderResult(body) {
  const r = currentResult || {};
  const e = r.extracted || {};
  const p = r.appealPotential || {};
  const score = Number(p.score) || 0;
  const scoreColor = score >= 60 ? '#10b981' : score >= 35 ? '#f59e0b' : '#ef4444';
  const scoreLabel = score >= 60 ? 'סיכוי גבוה' : score >= 35 ? 'סיכוי בינוני' : 'סיכוי נמוך';
  const reasons = Array.isArray(p.reasons) ? p.reasons : [];

  body.innerHTML = `
    <div class="fine-result">

      <!-- Extracted summary card -->
      <div class="fine-card fine-card-summary">
        <div class="fine-card-title">📋 פרטי הקנס</div>
        <div class="fine-fields">
          ${field('סוג עבירה', e.violationType)}
          ${field('סכום', e.amount ? `${esc(String(e.amount))} ₪` : '')}
          ${field('תאריך', e.issuedDate)}
          ${field('שעה', e.issuedTime)}
          ${field('מקום', e.location)}
          ${field('סעיף', e.section)}
          ${field('מספר קנס', e.fineNumber)}
          ${field('דדליין', e.deadline)}
          ${field('רשות מנפיקה', e.issuingAuthority)}
        </div>
      </div>

      <!-- Appeal potential card -->
      <div class="fine-card fine-card-score">
        <div class="fine-card-title">🎯 סיכוי ערעור</div>
        <div class="fine-score-wrap">
          <div class="fine-score-circle" style="--score-color:${scoreColor};">
            <span class="fine-score-num">${score}%</span>
            <span class="fine-score-label">${esc(scoreLabel)}</span>
          </div>
          <div class="fine-score-side">
            <div class="fine-score-advice">${esc(String(p.advice || ''))}</div>
            ${reasons.length ? `
              <ul class="fine-score-reasons">
                ${reasons.map((rs) => `<li>${esc(String(rs))}</li>`).join('')}
              </ul>
            ` : ''}
          </div>
        </div>
      </div>

      <!-- Appeal letter card -->
      <div class="fine-card fine-card-letter">
        <div class="fine-card-title">
          ✉️ מכתב הערעור
          <span class="fine-card-subtitle">ערוך לפי הצורך לפני ההגשה</span>
        </div>
        <textarea class="fine-letter-text" id="fineLetterText"
                  rows="14" spellcheck="false">${esc(String(r.appealLetter || ''))}</textarea>
        <div class="fine-letter-actions">
          <button type="button" class="fine-action fine-action-copy" id="fineCopyBtn">
            📋 העתק
          </button>
          <button type="button" class="fine-action fine-action-download" id="fineDownloadBtn">
            ⬇️ הורד .txt
          </button>
          <button type="button" class="fine-action fine-action-share" id="fineShareBtn">
            📤 שתף
          </button>
        </div>
        <div class="fine-letter-hint">
          📌 להגשה — אתר משרד התחבורה (לקנסות תנועה) או אתר העירייה הרלוונטית (לחניה).
        </div>
      </div>

      <div class="fine-result-actions">
        <button type="button" class="fine-retry" id="fineRetry">📸 נסה תמונה אחרת</button>
      </div>
    </div>
  `;
  body.querySelector('#fineCopyBtn')?.addEventListener('click', onCopy);
  body.querySelector('#fineDownloadBtn')?.addEventListener('click', onDownload);
  body.querySelector('#fineShareBtn')?.addEventListener('click', onShare);
  body.querySelector('#fineRetry')?.addEventListener('click', () => { step = 'upload'; render(); });
}

function field(label, value) {
  const v = String(value || '').trim();
  if (!v || v === 'לא קריא' || v === 'לא ידוע') return '';
  return `
    <div class="fine-field">
      <div class="fine-field-label">${esc(label)}</div>
      <div class="fine-field-value">${esc(v)}</div>
    </div>
  `;
}

function renderError(body) {
  body.innerHTML = `
    <div class="fine-error">
      <div class="fine-error-emoji">😕</div>
      <div class="fine-error-text">${esc(currentError || 'אירעה שגיאה')}</div>
      <button type="button" class="fine-retry" id="fineErrRetry">📸 נסה שוב</button>
    </div>
  `;
  body.querySelector('#fineErrRetry')?.addEventListener('click', () => { step = 'upload'; render(); });
}

async function onFilePicked(e) {
  const input = /** @type {HTMLInputElement} */ (e.currentTarget || e.target);
  const file = input.files && input.files[0];
  input.value = '';
  if (!file || !file.type.startsWith('image/')) return;

  step = 'analysing';
  render();

  try {
    const prepared = await prepareImage(file);
    const { analysis, error } = await service.analyze(prepared);
    if (error) {
      currentError = error;
      step = 'error';
      render();
      return;
    }
    currentResult = analysis;
    step = 'result';
    render();
  } catch (err) {
    log.warn('analyze failed:', err);
    const m = String(err?.message || '');
    currentError = /resource-exhausted|מכסה/i.test(m)
      ? 'הגעת למכסה היומית של AutoMe AI. נסה שוב מחר.'
      : 'אירעה שגיאה. נסה שוב עם תמונה אחרת.';
    step = 'error';
    render();
  }
}

function onCopy() {
  const ta = /** @type {HTMLTextAreaElement | null} */ (document.getElementById('fineLetterText'));
  if (!ta) return;
  try {
    ta.select();
    document.execCommand('copy');
    flash('הועתק ללוח 📋');
  } catch (_) { /* ignore */ }
}

function onDownload() {
  const ta = /** @type {HTMLTextAreaElement | null} */ (document.getElementById('fineLetterText'));
  if (!ta) return;
  const text = ta.value;
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `מכתב-ערעור-${new Date().toISOString().slice(0, 10)}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function onShare() {
  const ta = /** @type {HTMLTextAreaElement | null} */ (document.getElementById('fineLetterText'));
  if (!ta) return;
  const text = ta.value;
  const nav = /** @type {any} */ (navigator);
  if (nav.share) {
    try {
      await nav.share({ title: 'מכתב ערעור — AutoMe', text });
    } catch (_) { /* user cancelled */ }
  } else {
    onCopy();
  }
}

function flash(msg) {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  const t = document.createElement('div');
  t.className = 'fine-toast';
  t.textContent = msg;
  overlay.appendChild(t);
  setTimeout(() => t.remove(), 2000);
}
